import { type MouseEvent, forwardRef, useMemo, useState } from "react";
import { c, t } from "ttag";

import { Link, type LinkProps } from "metabase/common/components/Link";
import { ToolbarButton } from "metabase/common/components/ToolbarButton";
import { canAccessDataStudio } from "metabase/common/data-studio/selectors";
import { openEventsSidebar } from "metabase/dashboard/actions";
import { useDashboardContext } from "metabase/dashboard/context/context";
import { useRefreshDashboard } from "metabase/dashboard/hooks";
import { useRegisterShortcut } from "metabase/palette/hooks/useRegisterShortcut";
import {
  PLUGIN_CACHING,
  PLUGIN_LIBRARY,
  PLUGIN_MODERATION,
} from "metabase/plugins";
import { useDispatch, useSelector } from "metabase/redux";
import { useLocation } from "metabase/router";
import { Icon, Menu } from "metabase/ui";
import * as Urls from "metabase/urls";
import { parseSearchQuery } from "metabase/utils/browser";

import {
  AutoRefreshMenuItem,
  AutoRefreshMenuOptions,
} from "../../RefreshWidget";

type DashboardActionMenuProps = {
  canResetFilters: boolean;
  onResetFilters: () => void;
  canEdit: boolean;
  openSettingsSidebar: () => void;
};

// Fixes this bug: https://github.com/mantinedev/mantine/issues/5571#issue-2082430353
// Hover states get weird when using Link directly. Since Link does not take the standard
// `ref` prop, we have to manually forward it to the correct prop name to make hover work as expected.
const ForwardRefLink = forwardRef((props: LinkProps, ref) => (
  // @ts-expect-error - innerRef not in prop types but it is a valid prop. docs can be found here: https://github.com/remix-run/react-router/blob/v3.2.6/docs/API.md#innerref
  <Link {...props} innerRef={ref} />
));
// @ts-expect-error - must set a displayName + this works
ForwardRefLink.displayName = "ForwardRefLink";

const DashboardActionMenuInner = ({
  canResetFilters,
  onResetFilters,
  canEdit,
  openSettingsSidebar,
}: DashboardActionMenuProps) => {
  const location = useLocation();
  const dispatch = useDispatch();
  const {
    dashboard,
    isFullscreen,
    onFullscreenChange,
    onChangeLocation,
    withTimelineEvents = false,
  } = useDashboardContext();
  const hasDataStudioAccess = useSelector(canAccessDataStudio);
  const [opened, setOpened] = useState(false);
  const [showAutoRefreshOptions, setShowAutoRefreshOptions] = useState(false);

  const handleOpenChange = (isOpen: boolean) => {
    setOpened(isOpen);
    if (!isOpen) {
      setShowAutoRefreshOptions(false);
    }
  };

  const parameterQueryParams = useMemo(
    () => parseSearchQuery(location?.search ?? ""),
    [location?.search],
  );

  const { refreshDashboard } = useRefreshDashboard({
    dashboardId: dashboard?.id ?? null,
    parameterQueryParams,
  });

  const moderationItems = PLUGIN_MODERATION.useDashboardMenuItems(
    dashboard ?? undefined,
    refreshDashboard,
  );

  // solely for the dependency list below, so we don't ever have an undefined
  const pathname = location?.pathname ?? "";
  useRegisterShortcut(
    [
      {
        id: "dashboard-send-to-trash",
        perform: () => {
          if (pathname) {
            onChangeLocation(`${pathname}/archive`);
          }
        },
      },
    ],
    [pathname],
  );

  if (!dashboard) {
    return null;
  }

  const canConfigureCaching =
    dashboard.can_set_cache_policy && PLUGIN_CACHING.isGranularCachingEnabled();
  const showDataStudioLink =
    hasDataStudioAccess &&
    PLUGIN_LIBRARY.isLibraryCollectionType(dashboard.collection?.type);

  return (
    <Menu position="bottom-end" opened={opened} onChange={handleOpenChange}>
      <Menu.Target>
        <div>
          <ToolbarButton
            icon="ellipsis"
            aria-label={t`Move, trash, and more…`}
            tooltipLabel={t`Move, trash, and more…`}
          />
        </div>
      </Menu.Target>
      <Menu.Dropdown>
        {showAutoRefreshOptions ? (
          <AutoRefreshMenuOptions onSelect={() => handleOpenChange(false)} />
        ) : (
          <>
            {showDataStudioLink && (
              <>
                <Menu.Item
                  leftSection={<Icon name="grid_bordered" />}
                  component={ForwardRefLink}
                  to={Urls.dataStudioDashboard(dashboard.id)}
                >{t`Open in Data Studio`}</Menu.Item>
                <Menu.Divider />
              </>
            )}

            {canResetFilters && (
              <Menu.Item
                leftSection={<Icon name="revert" />}
                onClick={onResetFilters}
              >
                {t`Reset all filters`}
              </Menu.Item>
            )}

            <Menu.Item
              leftSection={<Icon name="expand" />}
              onClick={(e: MouseEvent) =>
                onFullscreenChange(!isFullscreen, !e.altKey)
              }
            >
              {t`Enter fullscreen`}
            </Menu.Item>

            <AutoRefreshMenuItem
              onClick={() => setShowAutoRefreshOptions(true)}
            />

            {/* fullscreen hides every sidebar, so the panel this opens could never appear */}
            {withTimelineEvents && !isFullscreen && (
              <Menu.Item
                leftSection={<Icon name="calendar" />}
                onClick={() =>
                  dispatch(openEventsSidebar({}, "dashboard_menu"))
                }
              >
                {t`Events`}
              </Menu.Item>
            )}

            {(canEdit || canConfigureCaching) && (
              <Menu.Item
                leftSection={<Icon name="gear" />}
                onClick={openSettingsSidebar}
              >
                {t`Edit settings`}
              </Menu.Item>
            )}

            {canEdit && moderationItems}

            {canEdit && (
              <>
                <Menu.Divider />

                <Menu.Item
                  leftSection={<Icon name="move" />}
                  component={ForwardRefLink}
                  to={`${location?.pathname}/move`}
                >{c("A verb, not a noun").t`Move`}</Menu.Item>
              </>
            )}

            <Menu.Item
              leftSection={<Icon name="clone" />}
              component={ForwardRefLink}
              to={`${location?.pathname}/copy`}
            >{c("A verb, not a noun").t`Duplicate`}</Menu.Item>

            {canEdit && (
              <>
                <Menu.Divider />
                <Menu.Item
                  leftSection={<Icon name="trash" />}
                  component={ForwardRefLink}
                  to={`${location?.pathname}/archive`}
                >{t`Move to trash`}</Menu.Item>
              </>
            )}
          </>
        )}
      </Menu.Dropdown>
    </Menu>
  );
};

export const DashboardActionMenu = DashboardActionMenuInner;
